import { personNameKey } from "@/lib/person-mentions";
import { create } from "zustand";
import type { StudioNotebookContext, StudioProposal } from "@/lib/studio-proposal";
import type { AgentThreadDetail, AgentThreadRef, ResumeTourRef } from "@/lib/agent-sessions";
import type { AgentAction, AgentProgress } from "@/lib/agent-agui";
import { parseAgentNoticeCode, type AgentNoticeCode } from "@/lib/agent-notice";
import type { SearchSource } from "@/lib/research-sources";
import type { OrgRecordEntryDraft } from "@/lib/org-record-entry";

/** Same stage, same subject: every parameter but `count` matches. */
// `count`, `selected`, `matched` and `route` are what a later report ADDS to a line already drawn, so
// they are not part of its identity — a stage is the same stage whether or not its outcome is known
// yet. `matched` arrives when a listing was cut by the character budget and says how many there were
// (agent-service #114.2); it cannot be sent with the opening line because the total is not known
// until the query returns, so leaving it out here would draw a second line instead of merging.
// `route` is the company search's first decision — labels, summaries or both — and the same thing
// applies: the question is taken apart before anything is searched, so the opening line cannot
// carry it (agent-service 2026-10-07).
const OUTCOME_PARAMS = new Set(["count", "selected", "matched"]);

// `route` is an outcome of ONE phase, so it is scoped to that phase rather than added to the set
// above. A global entry would strip `route` from stage identity everywhere, and `route` is also an
// AgentMessage field (the tour navigation target) — a name worth not claiming for every phase on the
// strength of one that happens to send it.
const OUTCOME_PARAMS_BY_PHASE: Record<string, ReadonlySet<string>> = {
  planning_search: new Set(["route"]),
};

function isOutcomeParam(phase: string, key: string): boolean {
  return OUTCOME_PARAMS.has(key) || (OUTCOME_PARAMS_BY_PHASE[phase]?.has(key) ?? false);
}

/**
 * Does this report say how its stage turned out, or is it only announcing that the stage began?
 *
 * The VALUE has to be there, not just the key. `parseProgress` keeps empty-string params — it only
 * drops non-strings — and this stream demonstrably sends them, which is why `searching_web`,
 * `searching_pubmed` and `locating_place` all guard their own for emptiness. Keyed on the key alone,
 * an opening `{route: ""}` counted as an outcome, the real `{route: "field"}` that followed found no
 * mergeable line, and the trail drew two where it should draw one — the exact bug this fold exists to
 * prevent. The renderer reads `route` for truthiness, so this is the same definition of "not decided
 * yet" on both sides, which is the point.
 */
function hasOutcome(phase: string, params: Record<string, string> | undefined): boolean {
  return Object.entries(params ?? {}).some(([k, v]) => isOutcomeParam(phase, k) && v !== "");
}

/**
 * Add one progress line to a trail, merging it into the line it continues.
 *
 * A stage is reported twice: once when it starts, once with what it returned (agent-service sends
 * the same phase and params, the second time carrying `count`). The second REPLACES the line already
 * drawn instead of adding another, so "Durchsuche die Beschreibungen nach X" becomes "… – die 10
 * nächsten von 200" rather than being followed by a bare number. A third can arrive later still: how
 * many of a search's hits the model then chose. So a line is replaceable whether or not it has a
 * count — the guard is only that a MORE complete line is never replaced by a less complete one.
 * Matched from the end, because the same query can legitimately be reported more than once a turn.
 *
 * Exported because a RELOADED thread must fold the same events the same way: the server replays the
 * whole stream, and rendering it unfolded gave a twenty-line trail where the live one had ten (Max,
 * 2026-09-30). One rule, two callers — the only way the two views cannot drift apart.
 */
export function foldProgress(trail: AgentProgress[], incoming: AgentProgress): AgentProgress[] {
  // Keyed on HAVING an outcome, not on having a `count`: a routing decision reports `route` and no
  // number at all, and a guard that only knew about counts appended it as a second line.
  if (!hasOutcome(incoming.phase, incoming.params)) return [...trail, incoming];
  // A selection report replaces its line whether or not that line already carries one: the
  // investigation submits once per send-back, so the same search is credited again with what the
  // CORRECTED assessment chose. Requiring the target to have no selection yet appended a second
  // copy of every search instead (Max, 2026-09-30).
  const incomingHasSelection = incoming.params?.selected !== undefined;
  const at = trail.findLastIndex(
    (p) =>
      p.phase === incoming.phase &&
      (!hasOutcome(p.phase, p.params) || incomingHasSelection) &&
      sameParams(incoming.phase, p.params, incoming.params),
  );
  if (at < 0) return [...trail, incoming];
  const merged = [...trail];
  merged[at] = incoming;
  return merged;
}

function sameParams(phase: string, a: Record<string, string> | undefined, b: Record<string, string>): boolean {
  const without = (p: Record<string, string> | undefined) =>
    Object.entries(p ?? {}).filter(([k]) => !isOutcomeParam(phase, k));
  const left = without(a);
  const right = without(b);
  return left.length === right.length && left.every(([k, v]) => b[k] === v);
}

// UI + transient state for the Assistant (the in-app AI chat that streams
// from the agent-service via the BFF /ai/stream passthrough). Distinct from
// the person-to-person chat-store: the Assistant has no GraphQL conversation
// entity — messages are an in-memory thread, lost on reload (acceptable for
// v1). `agentThreadId` is the agent-service's conversation-memory id (its
// wire field is `sessionId`); resent across turns so the Assistant remembers
// context. It is NOT the BFF login Session id.

/**
 * Separates the part of a seeded prompt the user SEES from the payload only the model needs — a
 * catalogue, a brief. Everything after it is sent but never rendered in the bubble.
 *
 * It travels inside the message text because agent-service persists the user turn verbatim, which
 * has one visible cost: the session-list preview is `coalesce(title, left(user_message, 200))`, so a
 * fresh thread's label shows payload text until the async title generation (@adr SPARK-0010-no-raw-server-errors-in-ui@1) replaces it.
 * That is a transient list label, and it is the price of the model seeing enough to answer well —
 * weighed the other way once (c597012ae) and the recommendations got measurably vaguer.
 */
export const AGENT_CONTEXT_MARKER = "\n\n<<<context>>>\n";

/** A user message as it should be displayed: everything before [AGENT_CONTEXT_MARKER]. */
export function visibleUserText(content: string): string {
  const at = content.indexOf(AGENT_CONTEXT_MARKER);
  return at === -1 ? content : content.slice(0, at);
}

export interface AgentMessage {
  noticeCode?: AgentNoticeCode;
  // `info` is a user-triggered, non-LLM person profile load (@adr SPARK-0014-design-primitives-single-source@1): rendered as a
  // distinct info block, not a chat bubble. Streamed into like an assistant message.
  id: string;
  role: "user" | "assistant" | "info";
  content: string;
  error?: string;
  // Explicit border color (live tour bubbles: the theme-frozen pillar color). When absent, the
  // renderer derives the border from `section` instead.
  color?: string;
  // Tour hierarchy indent (0 = section intro, 1 = page summary, 2 = page stop) — tour bubbles
  // render indented so the outline structure is visible. Persisted with tours.
  indent?: number;
  // Which section the message belongs to (a turn's subsection like "growth"/"save/profile", or a
  // tour step's pillar like "grow"); the renderer maps it to the border color. Persisted.
  section?: string;
  // Tour-step revisit target: clicking the bubble navigates to `route` (if set) and pulses the
  // `[data-assistant-anchor=anchor]` element. Persisted with tours; also set on live tour bubbles.
  anchor?: string;
  route?: string;
  // The answer carries content researched on the public web
  // (@external-adr AGENT-SERVICE-0034-web-search-as-a-separate-tool). Set from RUN_FINISHED on a live
  // turn and from the session detail on reload; the drawer shows its external-content notice while
  // any message in the thread has it.
  externalSources?: boolean;
  // The turn's progress lines, kept with the answer when the turn ends: what the assistant did to
  // arrive at it. Live they scroll past and are cleared; the answer alone does not say that five
  // searches ran and one company was chosen out of nineteen. Not persisted server-side yet, so a
  // reloaded thread has none (agent-service keeps them in `agent_subgraph_steps`).
  thinking?: AgentProgress[];
  // Interactive cards a browser-executed tool attached to this answer (the Studio proposal). Live only:
  // the session detail does not carry tool calls, so a reloaded thread shows the text without them.
  cards?: AgentCard[];
  // What the turn did on its way to this answer, kept once it ends (the live `progress` empties), for a
  // view that shows the steps after the fact: the Studio notebook. Live only, like `cards`.
  steps?: AgentProgress[];
  // A prompt the Studio asked for a purpose of its own (the notebook's final version), carried from
  // `askInNotebook` to the user message the turn creates.
  studioPurpose?: "final";
}

// A card rendered under an assistant answer, one kind per browser-executed tool that needs more than a
// navigation. The data is validated before it gets here (assistant-ui-tools.ts).
// A second proposal in the same answer joins the first card; `mergedIds` remembers its tool call.
// `mergedIds`: the tool calls a merged proposal card also stands for, so a replayed call is not added twice.
export type AgentCard = { id: string; mergedIds?: string[] } & (
  | { kind: "studioProposal"; data: StudioProposal }
  | { kind: "orgRecordEntry"; data: OrgRecordEntryDraft }
);
// A card as a tool hands it over, before it has an id; one variant per kind (a plain Omit would merge them).
export type NewAgentCard = AgentCard extends infer Card ? (Card extends AgentCard ? Omit<Card, "id"> : never) : never;

// One "what the user currently sees" fact (AG-UI Context shape). Registered by the mounted page
// via useAssistantContext and merged into every turn's uiContext (@external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption, motive 1).
export interface AssistantContextItem {
  description: string;
  value: string;
}

// One stop of a page's guided tour (/tour): highlight the [data-assistant-anchor=anchor] element
// and post title+description in the chat. Registered by the mounted page via useAssistantTour with
// live data baked into the strings.
export interface TourStop {
  anchor: string;
  title: string;
  description: string;
  /** Hand-tuned display time for THIS stop, verbatim (skips the driver's base + reading-time). */
  dwellMs?: number;
}

export interface PageTour {
  route: string;
  // Short localized page name incl. its pillar ("Save Documents") — prefixes every tour bubble
  // so a snippet names the (sub)section it belongs to.
  label: string;
  summary: string;
  stops: TourStop[];
}

interface AgentState {
  open: boolean;
  // Where the next question may search: the web OR a research source, never both at once (Marvin, 2026-09-28),
  // null for nowhere. One state for the drawer's globe and the notebook's switches. It holds for ONE question:
  // sending takes it and the globe is off again, so a question leaves the system only when the user opened it
  // for that question (Marvin, 2026-09-25).
  searchSource: SearchSource | null;
  // The people the user picked where a reply asked which one they mean, by the name the reply wrote
  // (personNameKey) -> person id. A card that then looks that name up again picks the same person.
  personChoices: Record<string, string>;
  choosePerson: (askedName: string, personId: string) => void;
  setSearchSource: (source: SearchSource | null) => void;
  // The source for the question being sent, and the globe off again.
  takeSearchSource: () => SearchSource | null;
  // Which screen the window shows: the Chats list (history) or the active chat.
  // Defaults to the list ("land on history"); persists across minimize/reopen
  // (the panel stays mounted), resets to list only on reload.
  view: "list" | "chat";
  agentThreadId: string | null;
  // True while a turn is in flight — drives the typing dots and disables the
  // composer.
  pending: boolean;
  // The run the in-flight turn is, from RUN_STARTED. What Stop names: the turn the user is looking
  // at, rather than "whatever is running now" — which is the wrong turn if they press Stop as it
  // ends and have already asked the next question. Null when nothing is running.
  runId: string | null;
  messages: AgentMessage[];

  // Chat history (past agent threads) shown in the list view.
  threads: AgentThreadRef[];
  threadsState: "idle" | "loading" | "error";

  // Agent-generated follow-up suggestions for the latest reply (the clickable
  // chips). Ephemeral: set on the `suggestions` SSE event, cleared when a new
  // turn starts / on newChat / openThread.
  suggestions: string[];

  // The steps the running turn has reported, oldest first
  // (@external-adr AGENT-SERVICE-0030-assistant-progress-channel). A list, not one value: the whole
  // path a turn took ("counted this, found nobody, tallied that") is what makes a 40s wait legible.
  // Ephemeral, exactly like `suggestions`: appended on each `progress` event, emptied when a turn
  // starts or ends. Rendered in the awaiting message placeholder, so it is on screen only while the
  // answer is still empty.
  progress: AgentProgress[];

  // A prompt queued by `ask()` (e.g. the power-detail "ask the assistant" button) for the always-mounted
  // AssistantDrawer to send once it isn't mid-turn. null when nothing is queued. `action` is the
  // typed intent the button declares (AgentAction) — one object, so the text and its intent cannot
  // desync between queueing and draining.
  pendingSend: { text: string; action?: AgentAction } | null;

  // The Studio notebook whose own conversation the current chat is: a prompt typed in the notebook runs
  // there, shown in the notebook, without opening the drawer. null once any other entry point starts or
  // opens a chat.
  studioConversationFor: string | null;
  // The purpose of the Studio prompt queued in `pendingSend`, handed to its user message by `startTurn`.
  pendingStudioPurpose: "final" | null;

  // Bumped when an Assistant turn completes a write that changed the open Match's design
  // (SetMatchTeamSize/Notes/Conditions/Project). An open match editor watches this to reconcile its
  // local overlay with the server truth the agent just wrote — otherwise the editor's stale
  // whole-blob autosave clobbers the agent's field-scoped change (genesis#167 concurrent-write caveat).
  agentWriteEpoch: number;

  // Bumped when an Assistant turn ran the solver (GenerateTransientMatchResult) for a match. The
  // solve persisted a draft result server-side; that match's editor watches this to switch to the
  // Result step and LOAD that draft (never re-solving — the solve is expensive/non-deterministic).
  // Scoped by matchSolveMatchId so a turn bound to match A can't trip match B's editor after the
  // user navigates mid-turn — the editor reacts only when matchSolveMatchId is its own id.
  matchSolveEpoch: number;
  matchSolveMatchId: string | null;

  // Anchor name of an element the agent asked to highlight (HighlightElement tool, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption).
  // Consumed by the AssistantDrawer effect: scrollIntoView + pulse class, cleared after ~4s.
  highlightAnchor: string | null;
  // Which match-editor step the user is on, mapped to the agent `section` suffix (`match/setup` vs
  // `match/results`) so the assistant is offered setup vs results tools. null when off the match
  // editor (bare `match`). Set by MatchDetailPage from the editor step, cleared on unmount.
  matchSubsection: "setup" | "results" | null;
  // Snippet text shown as a floating card at the highlighted element while it pulses — set by
  // snippet clicks alongside highlightAnchor; null for plain agent highlights.
  highlightHint: { text: string } | null;

  // Live page context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption), keyed by the registering component's id. The mounted
  // page(s) write via useAssistantContext; the AssistantDrawer reads the flattened values at send
  // time (imperatively — NOT a reactive selector — so page-data churn never re-renders the drawer).
  uiContext: Record<string, AssistantContextItem[]>;
  // The Studio notebook the user has open (@external-adr AGENT-SERVICE-0036-the-studio-proposes-records-the-user-decides):
  // set by the Studio page, sent with every save/studio turn, cleared when the notebook closes.
  studioNotebook: StudioNotebookContext | null;

  // Guided-tour registrations (/tour), keyed by the registering component's id — same lifecycle as
  // uiContext (mounted page registers, replaces on data change, clears on unmount). The tour driver
  // reads these imperatively.
  pageTours: Record<string, PageTour>;
  // True while a /tour run is in flight; swaps the chips strip to the tour controls and reroutes Esc.
  tourActive: boolean;
  // True while the running tour is paused (Pause/Continue button label + driver gate).
  tourPaused: boolean;
  // True once the tour has finished and sits in review mode (controls stay, snippets clickable;
  // Stop/Esc exits). Distinct from an abort, which tears everything down immediately.
  tourFinished: boolean;
  // Message ids of the running tour's shown steps — those bubbles are clickable (revisit).
  // Render-safe mirror of the driver's step history (refs must not be read during render).
  tourStepIds: string[];
  // The step currently on screen (live position or a revisit target) — its bubble gets the
  // extra "you are here" highlight.
  tourCurrentStepId: string | null;
  // Dwell (ms) the current LIVE step is being shown for — the countdown bar fills over it so the
  // user sees the next step approaching. 0 = no countdown (revisit / review / teardown → bar hidden).
  tourStepDwellMs: number;
  // Mirror of the LOADED session's unfinished-tour state — no other source. Set from the
  // session detail on openThread (or locally right after a live interruption, which is the
  // same state the detail would report); cleared whenever no session with an interrupted tour
  // is loaded. The continue controls render only while this is set.
  resumeTour: ResumeTourRef | null;

  setOpen: (open: boolean) => void;
  toggleOpen: () => void;
  setAgentThreadId: (id: string) => void;
  setRunId: (runId: string | null) => void;
  setHighlightAnchor: (anchor: string | null) => void;
  setMatchSubsection: (subsection: "setup" | "results" | null) => void;
  setHighlightHint: (hint: { text: string } | null) => void;
  // Register/replace the calling component's live context items; clear removes them (on unmount).
  setUiContext: (id: string, items: AssistantContextItem[]) => void;
  setStudioNotebook: (notebook: StudioNotebookContext | null) => void;
  clearUiContext: (id: string) => void;
  setPageTour: (id: string, tour: PageTour) => void;
  clearPageTour: (id: string) => void;
  setTourActive: (active: boolean) => void;
  setTourPaused: (paused: boolean) => void;
  setTourFinished: (finished: boolean) => void;
  addTourStepId: (id: string) => void;
  clearTourStepIds: () => void;
  setTourCurrentStepId: (id: string | null, dwellMs?: number) => void;
  setResumeTour: (resume: ResumeTourRef | null) => void;
  // Push one COMPLETE message (no pending/typing-dots) — the deterministic tour narration channel.
  // Returns the new message id (the tour driver links bubbles to revisitable steps by it).
  appendMessage: (
    role: AgentMessage["role"],
    content: string,
    opts?: Pick<AgentMessage, "color" | "indent" | "section" | "anchor" | "route">,
  ) => string;
  showList: () => void;
  setThreads: (threads: AgentThreadRef[]) => void;
  setThreadsState: (state: "idle" | "loading" | "error") => void;
  setSuggestions: (suggestions: string[]) => void;
  addProgress: (progress: AgentProgress) => void;
  signalAgentWrite: () => void;
  signalMatchSolve: (matchId: string) => void;
  // Drop a deleted thread from the list; if it's the one currently loaded,
  // reset the active chat so a later send doesn't resend a dead thread id.
  removeThread: (id: string) => void;
  // Start a fresh chat: empty thread, no id, switch to the chat view.
  newChat: () => void;
  // Open the drawer and queue `text` to be sent (drained by AssistantDrawer). Appends to the open chat
  // if one is open (chat view + messages); otherwise starts a new chat first.
  // `opts.hiddenContext` is sent to the model but not shown: see AGENT_CONTEXT_MARKER.
  // `opts.action` is a typed intent forwarded out-of-band (forwardedProps.action) — NOT smuggled
  // into the message text, so the user's bubble stays exactly what they see.
  ask: (text: string, opts?: { hiddenContext?: string; action?: AgentAction }) => void;
  // Opens the chat without sending anything: an ongoing conversation stays, otherwise a fresh one starts,
  // exactly as `ask` decides it. For a page that only hands context over (the Studio notebook).
  openChat: () => void;
  askInNotebook: (notebookId: string, text: string, purpose?: "final") => void;
  clearPendingSend: () => void;
  // Load a past thread's transcript into the chat view and continue it.
  openThread: (detail: AgentThreadDetail) => void;
  // Pushes the user message + an empty assistant message; returns the
  // assistant message id so the caller can stream deltas (or set the answer)
  // into it.
  startTurn: (userText: string, section?: string) => string;
  // Pushes a single empty `info` message (a profile load — no user bubble) and
  // returns its id so the caller can stream the profile into it. Sets `pending`
  // so the composer is disabled while it loads; leaves `suggestions` untouched.
  startInfoStream: () => string;
  appendDelta: (assistantId: string, delta: string) => void;
  setNotice: (assistantId: string, code: AgentNoticeCode) => void;
  // Flags the turn's answer as carrying public-web content (RUN_FINISHED said so).
  markExternalSources: (assistantId: string) => void;
  addCard: (assistantId: string, card: AgentCard) => void;
  endTurn: (assistantId?: string) => void;
  // Keeps the user message and any partial assistant text; records the error
  // on the assistant message and re-enables input.
  failTurn: (assistantId: string, error: string) => void;
}

function newId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

export const useAgentStore = create<AgentState>((set, get) => ({
  open: false,
  searchSource: null,
  personChoices: {},
  choosePerson: (askedName, personId) =>
    set((state) => ({ personChoices: { ...state.personChoices, [personNameKey(askedName)]: personId } })),
  setSearchSource: (searchSource) => set({ searchSource }),
  takeSearchSource: () => {
    const taken = get().searchSource;
    if (taken) set({ searchSource: null });
    return taken;
  },
  view: "list",
  agentThreadId: null,
  runId: null,
  pending: false,
  messages: [],
  threads: [],
  threadsState: "idle",
  suggestions: [],
  progress: [],
  pendingSend: null,
  studioConversationFor: null,
  pendingStudioPurpose: null,
  agentWriteEpoch: 0,
  matchSolveEpoch: 0,
  matchSolveMatchId: null,
  highlightAnchor: null,
  matchSubsection: null,
  highlightHint: null,
  uiContext: {},
  studioNotebook: null,
  pageTours: {},
  tourActive: false,
  tourPaused: false,
  tourFinished: false,
  tourStepIds: [],
  tourCurrentStepId: null,
  tourStepDwellMs: 0,
  resumeTour: null,

  setOpen: (open) => set({ open }),
  toggleOpen: () => set((s) => ({ open: !s.open })),
  setAgentThreadId: (agentThreadId) => set({ agentThreadId }),
  setRunId: (runId) => set({ runId }),
  setHighlightAnchor: (highlightAnchor) => set({ highlightAnchor }),
  setMatchSubsection: (matchSubsection) => set({ matchSubsection }),
  setHighlightHint: (highlightHint) => set({ highlightHint }),
  setUiContext: (id, items) => set((s) => ({ uiContext: { ...s.uiContext, [id]: items } })),
  setStudioNotebook: (studioNotebook) => set({ studioNotebook }),
  clearUiContext: (id) =>
    set((s) => {
      if (!(id in s.uiContext)) return s;
      const next = { ...s.uiContext };
      delete next[id];
      return { uiContext: next };
    }),
  setPageTour: (id, tour) => set((s) => ({ pageTours: { ...s.pageTours, [id]: tour } })),
  clearPageTour: (id) =>
    set((s) => {
      if (!(id in s.pageTours)) return s;
      const next = { ...s.pageTours };
      delete next[id];
      return { pageTours: next };
    }),
  setTourActive: (tourActive) => set({ tourActive }),
  setTourPaused: (tourPaused) => set({ tourPaused }),
  setTourFinished: (tourFinished) => set({ tourFinished }),
  addTourStepId: (id) => set((s) => ({ tourStepIds: [...s.tourStepIds, id] })),
  clearTourStepIds: () => set({ tourStepIds: [] }),
  setTourCurrentStepId: (tourCurrentStepId, dwellMs = 0) =>
    set({ tourCurrentStepId, tourStepDwellMs: dwellMs }),
  setResumeTour: (resumeTour) => set({ resumeTour }),
  appendMessage: (role, content, opts) => {
    const id = newId();
    set((s) => ({ messages: [...s.messages, { id, role, content, ...opts }] }));
    return id;
  },

  showList: () => set({ view: "list" }),
  setThreads: (threads) => set({ threads }),
  setThreadsState: (threadsState) => set({ threadsState }),
  setSuggestions: (suggestions) => set({ suggestions }),

  addProgress: (progress) => set((s) => ({ progress: foldProgress(s.progress, progress) })),
  signalAgentWrite: () => set((s) => ({ agentWriteEpoch: s.agentWriteEpoch + 1 })),
  signalMatchSolve: (matchId) =>
    set((s) => ({ matchSolveEpoch: s.matchSolveEpoch + 1, matchSolveMatchId: matchId })),

  removeThread: (id) =>
    set((s) => ({
      threads: s.threads.filter((t) => t.id !== id),
      ...(s.agentThreadId === id ? { agentThreadId: null, messages: [], resumeTour: null } : {}),
    })),

  // Does not touch `pending`: any background turn keeps running and clears
  // `pending` itself when it ends.
  newChat: () =>
    set({ view: "chat", agentThreadId: null, messages: [], suggestions: [], resumeTour: null, studioConversationFor: null }),

  // Open + queue a prompt. A chat is "open" only when the drawer is open, on the chat view, with
  // messages — then we append; otherwise (closed drawer, list view, or empty) we start a new chat.
  ask: (text, opts) => {
    const { open, view, messages } = get();
    const full = opts?.hiddenContext ? `${text}${AGENT_CONTEXT_MARKER}${opts.hiddenContext}` : text;
    const chatOpen = open && view === "chat" && messages.length > 0;
    set({
      open: true,
      view: "chat",
      pendingSend: { text: full, ...(opts?.action ? { action: opts.action } : {}) },
      ...(chatOpen ? {} : { agentThreadId: null, messages: [], suggestions: [], resumeTour: null, studioConversationFor: null }),
    });
  },
  openChat: () => {
    const { open, view, messages } = get();
    const chatOpen = open && view === "chat" && messages.length > 0;
    set({
      open: true,
      view: "chat",
      ...(chatOpen ? {} : { agentThreadId: null, messages: [], suggestions: [], resumeTour: null, studioConversationFor: null }),
    });
  },
  // Queues the prompt for the always-mounted drawer, which sends it whether it is open or not; the
  // notebook renders the turn from this store. Continues the notebook's conversation, else starts one.
  askInNotebook: (notebookId, text, purpose) => {
    const { studioConversationFor, messages } = get();
    const continuing = studioConversationFor === notebookId && messages.length > 0;
    set({
      view: "chat",
      pendingSend: { text },
      pendingStudioPurpose: purpose ?? null,
      studioConversationFor: notebookId,
      ...(continuing ? {} : { agentThreadId: null, messages: [], suggestions: [], resumeTour: null }),
    });
  },
  clearPendingSend: () => set({ pendingSend: null }),

  openThread: (detail) =>
    set({
      studioConversationFor: null,
      view: "chat",
      agentThreadId: detail.id,
      // Carry the persisted section (border color) + tour-step structure (indent, click target),
      // so reloaded threads render colored and stored tours stay interactive.
      messages: detail.messages.map((m) => ({
        id: newId(),
        role: m.role,
        content: m.content,
        section: m.section,
        indent: m.indent ?? undefined,
        anchor: m.anchor ?? undefined,
        route: m.route ?? undefined,
        externalSources: m.externalSources ? true : undefined,
        // The kept progress lines of that turn: the disclosure on the bubble shows the same trail
        // after a reload as it did live. `params` defaults because a phase can have none.
        // Folded exactly as the live trail was (foldProgress), so a reloaded answer shows the same
        // lines it showed while it was being written — not the raw event stream behind them.
        thinking: m.thinking?.length
          ? m.thinking
              .map((t) => ({ phase: t.phase, params: t.params ?? {}, depth: t.depth ?? 0, filter: t.filter }))
              .reduce<AgentProgress[]>((trail, line) => foldProgress(trail, line), [])
          : undefined,
        noticeCode: m.role === "assistant" ? parseAgentNoticeCode(m.noticeCode) : undefined,
      })),
      suggestions: detail.suggestions ?? [],
      // The loaded session decides: unfinished tour → cursor; anything else clears it.
      resumeTour: detail.resumeTour ?? null,
    }),

  startTurn: (userText, section) => {
    const assistantId = newId();
    set((s) => ({
      pending: true,
      suggestions: [],
      progress: [],
      pendingStudioPurpose: null,
      messages: [
        ...s.messages,
        { id: newId(), role: "user", content: userText, ...(s.pendingStudioPurpose ? { studioPurpose: s.pendingStudioPurpose } : {}) },
        // `section` drives the live border color of the incoming answer.
        { id: assistantId, role: "assistant", content: "", section },
      ],
    }));
    return assistantId;
  },

  startInfoStream: () => {
    const infoId = newId();
    set((s) => ({
      pending: true,
      // Sends no progress events, so the placeholder shows spark's own default label.
      progress: [],
      messages: [...s.messages, { id: infoId, role: "info", content: "" }],
    }));
    return infoId;
  },

  appendDelta: (assistantId, delta) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === assistantId ? { ...m, content: m.content + delta } : m,
      ),
    })),

  setNotice: (assistantId, noticeCode) =>
    set((s) => ({
      messages: s.messages.map((m) => m.id === assistantId ? { ...m, noticeCode } : m),
    })),

  markExternalSources: (assistantId) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === assistantId ? { ...m, externalSources: true } : m,
      ),
    })),

  addCard: (assistantId, card) =>
    set((s) => ({
      messages: s.messages.map((m) => {
        const cards = m.cards ?? [];
        if (m.id !== assistantId || cards.some((c) => c.id === card.id || c.mergedIds?.includes(card.id))) return m;
        // Proposals of records merge into one card; every draft for an organizational record is its own.
        const same = card.kind === "studioProposal" ? cards.find((c) => c.kind === "studioProposal") : undefined;
        if (!same || same.kind !== "studioProposal" || card.kind !== "studioProposal") return { ...m, cards: [...cards, card] };
        const merged: AgentCard = {
          ...same,
          mergedIds: [...(same.mergedIds ?? []), card.id],
          data: {
            persons: [...same.data.persons, ...card.data.persons],
            companies: [...same.data.companies, ...card.data.companies],
            rejected: [...same.data.rejected, ...card.data.rejected],
          },
        };
        return { ...m, cards: cards.map((c) => (c === same ? merged : c)) };
      }),
    })),

  // The progress list is cleared here, so this is the one place that can hand it to the answer —
  // and TWO views want it, for different reasons. `steps` feeds the Studio notebook, which shows
  // the turn's work after the fact; `thinking` feeds the disclosure on the bubble itself. Same
  // lines, two readers, so both are written here rather than one of them re-deriving them.
  endTurn: (assistantId) =>
    set((s) => {
      if (s.progress.length === 0) return { pending: false, progress: [], messages: s.messages };
      const lastAt = s.messages.length - 1;
      const messages = s.messages.map((m, i) => {
        // The notebook takes the last answer in the thread; the disclosure takes the answer this
        // turn was for, which the caller names. Normally the same message — but `failTurn` and a
        // tour can both leave a later one, so neither rule is expressed in terms of the other.
        const forNotebook = i === lastAt && m.role === "assistant";
        const forDisclosure = assistantId !== undefined && m.id === assistantId;
        if (!forNotebook && !forDisclosure) return m;
        return {
          ...m,
          ...(forNotebook ? { steps: s.progress } : {}),
          ...(forDisclosure ? { thinking: s.progress } : {}),
        };
      });
      return { pending: false, progress: [], messages };
    }),

  failTurn: (assistantId, error) =>
    set((s) => ({
      pending: false,
      progress: [],
      messages: s.messages.map((m) => (m.id === assistantId ? { ...m, error } : m)),
    })),
}));
